import { useEffect, useState, type CSSProperties } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { color } from '../styles/theme';
import { dateTime, shortDate } from '../lib/format';
import { useAuth } from '../auth/AuthContext';
import {
  INBOX_PAGE_SIZE,
  useInbox,
  useInboxReply,
  useInboxStatus,
  useInboxThread,
  type InboxFilter,
} from '../api/inbox';
import type { InboxMessage, InboxThread } from '../api/client';
import { Button, Card, Chip, ErrorNote, Field, Loading, Modal, RoundButton, inputStyle } from '../components/ui';

/**
 * The enact@ inbox (API 051), on the Messages page.
 *
 * People write to enact@enactresearch.com without filling in a form; the API
 * reads that mailbox and files each conversation here. An answer to an email
 * the CRM sent about a record goes on the record instead, so this is only
 * what came in on its own. Replies go from enact@, threaded under the
 * person's mail, and a conversation is done once answered — until they write
 * back.
 */

const ANSWER = { bg: '#e3f1e8', fg: '#14563c', edge: '#2f8a5c' };

const pill: CSSProperties = {
  fontSize: 10.5,
  fontWeight: 800,
  padding: '3px 9px',
  borderRadius: 999,
  whiteSpace: 'nowrap',
};

export function Inbox({ openId, onOpen }: { openId: string | null; onOpen: (id: string | null) => void }) {
  const [filter, setFilter] = useState<InboxFilter>('open');
  const [page, setPage] = useState(1);
  const list = useInbox(filter, page);
  useEffect(() => setPage(1), [filter]);

  const data = list.data;
  const pages = data ? Math.max(1, Math.ceil(data.total / INBOX_PAGE_SIZE)) : 1;

  return (
    <>
      <Card pad={20} style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        <p style={{ fontSize: 16, fontWeight: 800, color: color.ink, margin: 0 }}>
          Email inbox <span style={{ fontWeight: 600, color: color.muted, fontSize: 14 }}>· {data?.mailbox ?? 'enact@'}</span>
        </p>
        <p style={{ fontSize: 13, color: color.muted, margin: 0, lineHeight: 1.55 }}>
          Email people send to the lab without filling in a form. Answers to the CRM’s own emails go on the
          person’s record instead. Replies here go from {data?.mailbox ?? 'the mailbox'}.
        </p>
        {data && <ReadingLine reading={data.reading} />}
      </Card>

      <Card pad={0} style={{ overflow: 'hidden' }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            flexWrap: 'wrap',
            padding: '12px 20px',
            borderBottom: `1px solid ${color.line}`,
          }}
        >
          <Chip label={`Open${data ? ` ${data.counts.open}` : ''}`} active={filter === 'open'} onClick={() => setFilter('open')} />
          <Chip label={`Done${data ? ` ${data.counts.done}` : ''}`} active={filter === 'done'} onClick={() => setFilter('done')} />
          <Chip label="All" active={filter === 'all'} onClick={() => setFilter('all')} />
          {data?.redacted && (
            <span style={{ fontSize: 12, color: color.muted, marginLeft: 'auto' }}>
              Senders and messages are hidden for your role.
            </span>
          )}
        </div>

        {list.isLoading ? (
          <div style={{ padding: 20 }}>
            <Loading label="Loading the inbox…" />
          </div>
        ) : list.error ? (
          <div style={{ padding: 20 }}>
            <ErrorNote message={(list.error as Error).message} />
          </div>
        ) : !data || data.threads.length === 0 ? (
          <p style={{ fontSize: 13.5, color: color.muted, margin: 0, padding: '28px 20px', textAlign: 'center' }}>
            {filter === 'open' ? 'Nothing waiting. Every conversation has been answered or closed.' : 'No conversations here.'}
          </p>
        ) : (
          data.threads.map((t) => <ThreadRow key={t.id} thread={t} onOpen={() => onOpen(t.id)} />)
        )}

        {pages > 1 && (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 8, padding: '10px 20px' }}>
            <span style={{ fontSize: 12.5, color: color.muted }}>
              Page {page} of {pages}
            </span>
            <RoundButton size={30} title="Newer" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
              ‹
            </RoundButton>
            <RoundButton size={30} title="Older" disabled={page >= pages} onClick={() => setPage((p) => p + 1)}>
              ›
            </RoundButton>
          </div>
        )}
      </Card>

      {openId && <ThreadModal id={openId} onClose={() => onOpen(null)} />}
    </>
  );
}

/** Whether the mailbox is being read, and when it last was. */
function ReadingLine({ reading }: { reading: { reading: boolean; checkedAt: string | null; error: string | null } }) {
  const text = !reading.reading
    ? 'The mailbox is not read into the CRM here — its mail stays in Outlook.'
    : reading.error
      ? `The mailbox cannot be read yet: ${reading.error}`
      : reading.checkedAt
        ? `Checked every two minutes · last ${dateTime(reading.checkedAt)}.`
        : 'Not checked yet.';
  return (
    <p style={{ fontSize: 12, margin: 0, color: reading.error ? color.red : color.muted, fontWeight: reading.error ? 600 : 500 }}>
      {text}
    </p>
  );
}

function ThreadRow({ thread: t, onOpen }: { thread: InboxThread; onOpen: () => void }) {
  const who = t.fromName || t.fromAddress || 'Hidden for your role';
  return (
    <button
      onClick={onOpen}
      className="hov-row"
      style={{
        display: 'grid',
        gridTemplateColumns: '10px minmax(0,1fr) auto',
        gap: 12,
        alignItems: 'start',
        width: '100%',
        textAlign: 'left',
        background: 'transparent',
        border: 'none',
        borderBottom: `1px solid ${color.line}`,
        padding: '14px 20px',
        cursor: 'pointer',
        font: 'inherit',
      }}
    >
      <span
        aria-label={t.unread ? 'Unread' : undefined}
        style={{
          width: 8,
          height: 8,
          borderRadius: '50%',
          marginTop: 6,
          background: t.unread ? color.blue : 'transparent',
        }}
      />
      <span style={{ minWidth: 0, display: 'flex', flexDirection: 'column', gap: 2 }}>
        <span style={{ display: 'flex', gap: 8, alignItems: 'baseline', minWidth: 0 }}>
          <span
            style={{
              fontSize: 13.5,
              fontWeight: t.unread ? 800 : 600,
              color: color.ink,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {who}
          </span>
          {t.fromName && t.fromAddress && (
            <span style={{ fontSize: 12, color: color.muted, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {t.fromAddress}
            </span>
          )}
        </span>
        <span style={{ fontSize: 13, fontWeight: t.unread ? 700 : 500, color: color.ink }}>
          {t.subject ?? '—'}
          {t.messageCount > 1 && <span style={{ color: color.muted, fontWeight: 600 }}> · {t.messageCount}</span>}
        </span>
        {t.snippet && (
          <span
            style={{
              fontSize: 12.5,
              color: color.muted,
              display: '-webkit-box',
              WebkitLineClamp: 1,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
            }}
          >
            {t.snippet}
          </span>
        )}
      </span>
      <span style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 6 }}>
        <span style={{ fontSize: 12, color: color.muted, whiteSpace: 'nowrap' }}>{shortDate(t.lastMessageAt)}</span>
        <span
          style={{
            ...pill,
            background: t.status === 'open' ? color.tint : '#e6eaef',
            color: t.status === 'open' ? color.blueDeep : color.muted,
          }}
        >
          {t.status === 'open' ? 'Open' : 'Done'}
        </span>
      </span>
    </button>
  );
}

function ThreadModal({ id, onClose }: { id: string; onClose: () => void }) {
  const qc = useQueryClient();
  const { can } = useAuth();
  const thread = useInboxThread(id);
  const reply = useInboxReply();
  const setStatus = useInboxStatus();
  const [body, setBody] = useState('');
  const [done, setDone] = useState<string | null>(null);

  // Opening it reads it: the list's dot and the rail's count move.
  useEffect(() => {
    if (thread.data) void qc.invalidateQueries({ queryKey: ['inbox', 'list'] });
    if (thread.data) void qc.invalidateQueries({ queryKey: ['inbox', 'counts'] });
  }, [thread.data, qc]);

  const d = thread.data;
  const t = d?.thread;
  const canAct = can('submissions:update');
  const canReply = canAct && !!d && !d.redacted;

  async function send() {
    setDone(null);
    try {
      const res = await reply.mutateAsync({ id, body: { body: body.trim() } });
      setBody('');
      setDone(
        res.message.delivery === 'sent'
          ? `Sent to ${t?.fromAddress}. Marked done.`
          : res.message.delivery === 'logged'
            ? 'Logged — no email provider is configured here.'
            : 'The mail server refused it. It is on the conversation as failed — try again.',
      );
      void qc.invalidateQueries({ queryKey: ['inbox', 'thread', id] });
    } catch {
      /* surfaced through reply.error */
    }
  }

  async function toggle() {
    if (!t) return;
    await setStatus.mutateAsync({ id, body: { status: t.status === 'open' ? 'done' : 'open' } }).catch(() => {});
    void qc.invalidateQueries({ queryKey: ['inbox', 'thread', id] });
  }

  return (
    <Modal
      label="Email conversation"
      width={760}
      onClose={onClose}
      header={
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <p style={{ fontSize: 19, fontWeight: 800, color: color.ink, margin: 0 }}>
              {t ? (t.subject ?? 'Hidden for your role') : 'Loading…'}
            </p>
            {t && (
              <p style={{ fontSize: 12.5, color: color.muted, margin: '4px 0 0' }}>
                {[t.fromName, t.fromAddress && `<${t.fromAddress}>`].filter(Boolean).join(' ') || 'Sender hidden for your role'}
                {' · '}to {t.mailbox}
              </p>
            )}
          </div>
          {t && (
            <span
              style={{
                ...pill,
                background: t.status === 'open' ? color.tint : '#e6eaef',
                color: t.status === 'open' ? color.blueDeep : color.muted,
                marginTop: 4,
              }}
            >
              {t.status === 'open' ? 'Open' : 'Done'}
            </span>
          )}
        </div>
      }
      footer={
        canAct && t ? (
          <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
            {canReply && (
              <Button onClick={() => void send()} disabled={!body.trim() || reply.isPending}>
                {reply.isPending ? 'Sending…' : 'Send reply'}
              </Button>
            )}
            <Button variant="ghost" onClick={() => void toggle()} disabled={setStatus.isPending}>
              {t.status === 'open' ? 'Mark done' : 'Open again'}
            </Button>
            {done && <span style={{ fontSize: 12.5, color: color.muted, fontWeight: 600 }}>{done}</span>}
          </div>
        ) : undefined
      }
    >
      {thread.isLoading ? (
        <Loading label="Loading the conversation…" />
      ) : thread.error ? (
        <ErrorNote message={(thread.error as Error).message} />
      ) : d ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {d.messages.map((m) => (
            <MessageCard key={m.id} message={m} senderName={t?.fromName ?? null} />
          ))}

          {canReply ? (
            <div style={{ borderTop: `1px solid ${color.line}`, paddingTop: 14, display: 'flex', flexDirection: 'column', gap: 10 }}>
              <Field label="From">
                <input value={d.replyFrom} readOnly style={{ ...inputStyle, background: color.bg, color: color.muted }} />
              </Field>
              <Field label="Reply">
                <textarea
                  value={body}
                  rows={6}
                  onChange={(e) => {
                    setBody(e.target.value);
                    setDone(null);
                  }}
                  onKeyDown={(e) => {
                    // Escape closes the dialog; with words in the box that is a lost draft.
                    if (e.key === 'Escape' && body.trim()) e.stopPropagation();
                  }}
                  placeholder={`Write to ${t?.fromName || t?.fromAddress || 'them'}…`}
                  style={{ ...inputStyle, fontSize: 14, lineHeight: '22px', padding: '11px 13px', resize: 'vertical' }}
                />
              </Field>
              {!d.provider.configured && (
                <p style={{ fontSize: 12, fontWeight: 600, margin: 0, padding: '8px 12px', borderRadius: 10, background: '#f4f0e6', color: '#7a6234' }}>
                  No email provider here — replies are logged, not sent.
                </p>
              )}
              {reply.error && <ErrorNote message={(reply.error as Error).message} />}
            </div>
          ) : d.redacted ? (
            <p style={{ fontSize: 12.5, color: color.muted, margin: 0 }}>
              What was written is hidden for your role, so you cannot reply from here.
            </p>
          ) : null}
          {setStatus.error && <ErrorNote message={(setStatus.error as Error).message} />}
        </div>
      ) : null}
    </Modal>
  );
}

function MessageCard({ message: m, senderName }: { message: InboxMessage; senderName: string | null }) {
  const inbound = m.direction === 'in';
  const tone = m.delivery === 'failed' ? { bg: '#fdf1ee', fg: '#a4432f', label: 'Failed' } : m.delivery === 'logged' ? { bg: '#f4f0e6', fg: '#7a6234', label: 'Logged, not sent' } : null;
  return (
    <div
      style={{
        background: inbound ? color.white : color.bg,
        border: `1px solid ${color.line}`,
        borderLeft: `3px solid ${inbound ? ANSWER.edge : color.blue}`,
        borderRadius: 12,
        padding: '10px 12px',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <span style={{ ...pill, background: inbound ? ANSWER.bg : color.tint, color: inbound ? ANSWER.fg : color.blueDeep }}>
          {inbound ? 'They wrote' : 'Reply'}
        </span>
        <span style={{ fontSize: 12.5, fontWeight: 700, color: color.ink }}>
          {inbound ? (senderName || m.from || 'The sender') : (m.authorName ?? 'A former staff account')}
        </span>
        <span style={{ fontSize: 11.5, color: color.muted }}>{dateTime(m.at)}</span>
        <span style={{ flex: 1 }} />
        {tone && <span style={{ ...pill, background: tone.bg, color: tone.fg }}>{tone.label}</span>}
      </div>
      {(m.from || m.to) && (
        <p style={{ fontSize: 11.5, color: color.muted, margin: '4px 0 0' }}>
          {[m.from && `from ${m.from}`, m.to && `to ${m.to}`].filter(Boolean).join(' · ')}
        </p>
      )}
      {m.body !== null ? (
        <p style={{ fontSize: 13, lineHeight: '20px', color: color.ink, margin: '6px 0 0', whiteSpace: 'pre-wrap' }}>
          {m.body || '(No text — only what was attached.)'}
        </p>
      ) : (
        <p style={{ fontSize: 12, color: color.muted, margin: '6px 0 0' }}>Hidden for your role.</p>
      )}
      {m.hasAttachments && (
        <p style={{ fontSize: 12, color: color.muted, margin: '6px 0 0', fontWeight: 600 }}>
          It came with attachments — open it in Outlook to see them.
        </p>
      )}
    </div>
  );
}
